import React, { useState, useEffect } from 'react';
import { Toolbar, ToolbarSearch, ToolbarSelect, ToolbarSpacer, StatusBadge, RoleBadge, Banner, Pagination } from '@/components/AdminPanel/AdminPrimitives';
import { LCBtn, LCIcon, CLS } from '@/components/common/LCPrimitives';
import apiService from '@/services/apiService';
import { AuthUser } from '@/types/auth';
import { useAuth } from '@/contexts/AuthContext';
import UserCreateDialog from '@/components/AdminPanel/UserManagement/UserCreateDialog';
import UserEditDialog from '@/components/AdminPanel/UserManagement/UserEditDialog';
import ConfirmationDialog from '@/components/AdminPanel/UserManagement/ConfirmationDialog';
import PasswordResetDialog from '@/components/AdminPanel/UserManagement/PasswordResetDialog';

type ConfirmState = {
  open: boolean; title: string; message: string;
  onConfirm: () => void; severity?: 'warning' | 'error' | 'info';
};

const UserList: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<AuthUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AuthUser | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState>({
    open: false, title: '', message: '', onConfirm: () => {}, severity: 'warning',
  });
  const [pwReset, setPwReset] = useState({ open: false, email: '', password: '' });

  const fetchUsers = async () => {
    setLoading(true); setError(null);
    try {
      const params: any = { page: page + 1, page_size: pageSize };
      if (searchTerm) params.search = searchTerm;
      if (roleFilter) params.role = roleFilter;
      if (userTypeFilter) params.user_type = userTypeFilter;
      if (activeFilter) params.is_active = activeFilter === 'true';
      const data = await apiService.getUsers(params);
      setUsers(data.users); setTotal(data.total);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch users');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, [page, pageSize, searchTerm, roleFilter, userTypeFilter, activeFilter]);

  const isSuperUser = currentUser?.role === 'super';

  const handleEdit = (u: AuthUser) => { setSelectedUser(u); setEditOpen(true); };

  const handleResetPassword = (u: AuthUser) => setConfirm({
    open: true, title: 'Reset Password', severity: 'warning',
    message: `Reset password for ${u.email}? A new random password will be generated.`,
    onConfirm: async () => {
      setConfirm(p => ({ ...p, open: false }));
      try {
        const result = await apiService.resetUserPassword(u.id);
        setPwReset({ open: true, email: u.email, password: result.password });
      } catch (err: any) { setError(err.message || 'Failed to reset password'); }
    },
  });

  const handleDeactivate = (u: AuthUser) => setConfirm({
    open: true, title: 'Deactivate User', severity: 'error',
    message: `Deactivate ${u.email}? The user will not be able to log in until reactivated.`,
    onConfirm: async () => {
      setConfirm(p => ({ ...p, open: false }));
      try {
        await apiService.deactivateUser(u.id);
        setSuccess(`User ${u.email} deactivated`); fetchUsers();
      } catch (err: any) { setError(err.message || 'Failed to deactivate user'); }
    },
  });

  const handleActivate = (u: AuthUser) => setConfirm({
    open: true, title: 'Activate User', severity: 'info',
    message: `Activate ${u.email}? The user will be able to log in.`,
    onConfirm: async () => {
      setConfirm(p => ({ ...p, open: false }));
      try {
        await apiService.activateUser(u.id);
        setSuccess(`User ${u.email} activated`); fetchUsers();
      } catch (err: any) { setError(err.message || 'Failed to activate user'); }
    },
  });

  return (
    <div className="flex flex-col h-full min-h-0">
      {error && <Banner variant="error" onClose={() => setError(null)} className="mb-3">{error}</Banner>}
      {success && <Banner variant="success" onClose={() => setSuccess(null)} className="mb-3">{success}</Banner>}

      <Toolbar>
        <ToolbarSearch value={searchTerm} onChange={setSearchTerm} placeholder="Email, name, username…" />
        <ToolbarSelect value={roleFilter} onChange={setRoleFilter} placeholder="All roles"
          options={[{ value: 'admin', label: 'Admin' }, { value: 'super', label: 'Super' }, { value: 'normal', label: 'Normal' }]} />
        <ToolbarSelect value={userTypeFilter} onChange={setUserTypeFilter} placeholder="All types"
          options={[{ value: 'RM', label: 'RM' }, { value: 'IC', label: 'IC' }, { value: 'ARM', label: 'ARM' }, { value: 'ADMIN', label: 'ADMIN' }]} />
        <ToolbarSelect value={activeFilter} onChange={setActiveFilter} placeholder="All statuses"
          options={[{ value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' }]} />
        <ToolbarSpacer />
        <button className={CLS.iconBtn} onClick={fetchUsers} title="Refresh">
          <LCIcon name="refresh" size={14} />
        </button>
        {!isSuperUser && (
          <LCBtn variant="primary" size="sm" onClick={() => setCreateOpen(true)}>
            <LCIcon name="plus" size={12} /> Create User
          </LCBtn>
        )}
      </Toolbar>

      <div className="flex-1 min-h-0 flex flex-col border border-black/[0.08] bg-white">
        <div className="flex-1 min-h-0 overflow-auto">
          <table className={CLS.table}>
            <thead>
              <tr>
                <th className={CLS.th} style={{ width: 60 }}>ID</th>
                <th className={CLS.th}>Email</th>
                <th className={CLS.th}>Name</th>
                <th className={CLS.th}>Role</th>
                <th className={CLS.th}>Account scope</th>
                <th className={CLS.th}>Type</th>
                <th className={CLS.th}>Status</th>
                <th className={`${CLS.th} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className={`${CLS.td} ${CLS.muted} text-center py-8`}>Loading…</td></tr>
              )}
              {!loading && users.length === 0 && (
                <tr><td colSpan={8} className={`${CLS.td} ${CLS.muted} text-center py-8`}>No users found.</td></tr>
              )}
              {!loading && users.map(u => (
                <tr key={u.id} className="hover:bg-[#FAF9F8] transition-colors">
                  <td className={`${CLS.td} ${CLS.tdMono} text-[#7A7A76]`}>{u.id}</td>
                  <td className={CLS.td}>{u.email}</td>
                  <td className={CLS.td}>
                    {(u.first_name || u.last_name)
                      ? `${u.first_name || ''} ${u.last_name || ''}`.trim()
                      : '—'}
                  </td>
                  <td className={CLS.td}><RoleBadge role={u.role} /></td>
                  <td className={CLS.td}>
                    {u.role === 'normal' ? 'Assigned accounts' :
                      u.account_scope === 'avpu' ? 'AVPU only' :
                      u.account_scope === 'lcp' ? 'LCP only' : 'All accounts'}
                  </td>
                  <td className={CLS.td}>
                    <span className="text-[11px] font-semibold text-[#555] bg-black/[0.05] px-2 py-0.5">
                      {u.user_type}
                    </span>
                  </td>
                  <td className={CLS.td}><StatusBadge active={u.is_active} /></td>
                  <td className={`${CLS.td} text-right`}>
                    {isSuperUser ? (
                      <span className="text-[11px] text-[#7A7A76]">Read-only</span>
                    ) : (
                      <div className="flex items-center justify-end gap-0.5">
                        <button className={CLS.iconBtn} onClick={() => handleEdit(u)} title="Edit User">
                          <LCIcon name="edit" size={13} />
                        </button>
                        <button className={CLS.iconBtn} onClick={() => handleResetPassword(u)} title="Reset Password">
                          <LCIcon name="key" size={13} />
                        </button>
                        <button
                          className={`${CLS.iconBtn} ${u.is_active ? 'hover:text-[#E50025]' : 'hover:text-[#1f8a4a]'}`}
                          onClick={() => u.is_active ? handleDeactivate(u) : handleActivate(u)}
                          title={u.is_active ? 'Deactivate' : 'Activate'}
                        >
                          <LCIcon name={u.is_active ? 'close' : 'check'} size={13} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} pageSize={pageSize} total={total}
          onChange={(p, ps) => { setPage(p); setPageSize(ps); }} />
      </div>

      <UserCreateDialog open={createOpen} onClose={() => setCreateOpen(false)}
        onSuccess={() => { setSuccess('User created successfully'); fetchUsers(); }} />

      <UserEditDialog open={editOpen} user={selectedUser}
        onClose={() => { setEditOpen(false); setSelectedUser(null); }}
        onSuccess={() => { setSuccess('User updated successfully'); fetchUsers(); }} />

      <ConfirmationDialog
        open={confirm.open} title={confirm.title} message={confirm.message}
        severity={confirm.severity} onConfirm={confirm.onConfirm}
        onCancel={() => setConfirm(p => ({ ...p, open: false }))} />

      <PasswordResetDialog
        open={pwReset.open} email={pwReset.email} newPassword={pwReset.password}
        onClose={() => setPwReset({ open: false, email: '', password: '' })} />
    </div>
  );
};

export default UserList;
